import type { BlogArticle } from '../../types/blog'
import { BLOG_DATE_MODIFIED, BLOG_DATE_PUBLISHED } from './blogCommon'

export const toothacheArticle: BlogArticle = {
  slug: 'toothache-london-what-to-do',
  theme: 'red',
  datePublished: BLOG_DATE_PUBLISHED,
  dateModified: BLOG_DATE_MODIFIED,
  category: { en: 'Emergency', ua: 'Невідкладна допомога', ru: 'Неотложная помощь', ro: 'Urgențe' },
  readTime: { en: '7 min read', ua: '7 хв читання', ru: '7 мин чтения', ro: '7 min citire' },
  seoTitle: {
    en: 'Toothache in London: What to Do and When to See a Dentist | Dentico Clinic',
    ua: 'Болить зуб у Лондоні: що робити і коли йти до стоматолога | Dentico Clinic',
    ru: 'Болит зуб в Лондоне: что делать и когда идти к стоматологу | Dentico Clinic',
    ro: 'Durere de dinți în Londra: ce să faceți și când mergeți la dentist | Dentico Clinic',
  },
  seoDescription: {
    en: 'Step-by-step advice for toothache in London, including when to seek urgent dental help, what symptoms matter and what treatment may be needed.',
    ua: 'Покрокові поради при зубному болю в Лондоні: коли потрібна термінова допомога, на які симптоми звертати увагу і яке лікування може знадобитися.',
    ru: 'Пошаговые советы при зубной боли в Лондоне: когда нужна срочная помощь, на какие симптомы обращать внимание и какое лечение может понадобиться.',
    ro: 'Sfaturi pas cu pas pentru durerea de dinți în Londra: când aveți nevoie de ajutor urgent, ce simptome contează și ce tratament poate fi necesar.',
  },
  h1: {
    en: 'Toothache in London: what to do and when to see a dentist',
    ua: 'Болить зуб у Лондоні: що робити і коли йти до стоматолога',
    ru: 'Болит зуб в Лондоне: что делать и когда идти к стоматологу',
    ro: 'Durere de dinți în Londra: ce să faceți și când să mergeți la dentist',
  },
  cardTitle: {
    en: 'Toothache in London: what to do and when to see a dentist',
    ua: 'Болить зуб у Лондоні: що робити?',
    ru: 'Болит зуб в Лондоне: что делать?',
    ro: 'Durere de dinți în Londra: ce să faceți?',
  },
  cardExcerpt: {
    en: 'Safe first steps at home, symptoms that need urgent care, and what treatment a dentist may recommend for toothache.',
    ua: 'Безпечні перші кроки вдома, симптоми, що потребують термінової допомоги, і яке лікування може порадити стоматолог.',
    ru: 'Безопасные первые шаги дома, симптомы, требующие срочной помощи, и какое лечение может посоветовать стоматолог.',
    ro: 'Primii pași în siguranță acasă, simptomele care necesită îngrijire urgentă și ce tratament poate recomanda dentistul.',
  },
  intro: {
    en: 'Toothache rarely goes away on its own for good. Pain can come from decay, a cracked tooth, gum inflammation or an infection, and it often gets worse at night. This guide explains what you can safely do at home, which symptoms mean you should be seen urgently, and what treatment a dentist may recommend once the cause is found.',
    ua: 'Зубний біль рідко минає сам назавжди. Причиною може бути карієс, тріщина зуба, запалення ясен чи інфекція, і вночі біль часто посилюється. У цьому гіді пояснюємо, що можна безпечно зробити вдома, які симптоми означають, що потрібна термінова допомога, і яке лікування може запропонувати стоматолог після встановлення причини.',
    ru: 'Зубная боль редко проходит сама навсегда. Причиной может быть кариес, трещина зуба, воспаление дёсен или инфекция, и ночью боль часто усиливается. В этом гиде объясняем, что можно безопасно сделать дома, какие симптомы означают, что нужна срочная помощь, и какое лечение может предложить стоматолог после установления причины.',
    ro: 'Durerea de dinți rareori dispare definitiv de la sine. Poate fi cauzată de carii, un dinte fisurat, inflamația gingiilor sau o infecție și adesea se agravează noaptea. Acest ghid explică ce puteți face în siguranță acasă, ce simptome înseamnă că trebuie să fiți văzut urgent și ce tratament poate recomanda dentistul după identificarea cauzei.',
  },
  sections: [
    {
      heading: {
        en: 'When toothache needs urgent help',
        ua: 'Коли зубний біль потребує термінової допомоги',
        ru: 'Когда зубная боль требует срочной помощи',
        ro: 'Când durerea de dinți necesită ajutor urgent',
      },
      blocks: [
        {
          type: 'callout',
          tone: 'warning',
          text: {
            en: 'Call 999 or go to A&E if you have swelling of the face, mouth or neck that affects breathing, swallowing or your eyesight, or heavy bleeding that will not stop. For urgent dental advice out of hours, call NHS 111.',
            ua: 'Телефонуйте 999 або їдьте до A&E, якщо набряк обличчя, рота чи шиї заважає дихати, ковтати або впливає на зір, або якщо сильна кровотеча не зупиняється. Для термінової стоматологічної консультації в неробочий час телефонуйте NHS 111.',
            ru: 'Звоните 999 или езжайте в A&E, если отёк лица, рта или шеи мешает дышать, глотать или влияет на зрение, либо если сильное кровотечение не останавливается. Для срочной стоматологической консультации в нерабочее время звоните NHS 111.',
            ro: 'Sunați la 999 sau mergeți la A&E dacă aveți umflături ale feței, gurii sau gâtului care afectează respirația, înghițirea sau vederea, ori sângerare abundentă care nu se oprește. Pentru sfaturi dentare urgente în afara programului, sunați la NHS 111.',
          },
        },
        {
          type: 'p',
          text: {
            en: 'Contact a dentist the same day if you have severe or throbbing pain, pain that wakes you at night, swelling of the gum, a bad taste or pus, fever with tooth pain, or a tooth that has been broken or knocked out. An [emergency dentist in London](/emergency-dentist-london) can assess the cause and relieve the pain.',
            ua: 'Зверніться до стоматолога того ж дня, якщо біль сильний чи пульсуючий, будить вночі, є набряк ясен, неприємний присмак чи гній, температура разом із болем, або зуб зламався чи вибитий. [Невідкладний стоматолог у Лондоні](/emergency-dentist-london) визначить причину і полегшить біль.',
            ru: 'Обратитесь к стоматологу в тот же день, если боль сильная или пульсирующая, будит ночью, есть отёк десны, неприятный привкус или гной, температура вместе с болью, или зуб сломан либо выбит. [Неотложный стоматолог в Лондоне](/emergency-dentist-london) определит причину и облегчит боль.',
            ro: 'Contactați un dentist în aceeași zi dacă aveți durere severă sau pulsatilă, durere care vă trezește noaptea, gingie umflată, gust neplăcut sau puroi, febră cu durere de dinți, ori un dinte rupt sau căzut. Un [dentist de urgență în Londra](/emergency-dentist-london) poate evalua cauza și calma durerea.',
          },
        },
      ],
    },
    {
      heading: {
        en: 'What you can safely do at home',
        ua: 'Що можна безпечно зробити вдома',
        ru: 'Что можно безопасно сделать дома',
        ro: 'Ce puteți face în siguranță acasă',
      },
      blocks: [
        {
          type: 'steps',
          items: [
            {
              title: { en: 'Take suitable pain relief', ua: 'Прийміть знеболювальне', ru: 'Примите обезболивающее', ro: 'Luați un analgezic potrivit' },
              text: {
                en: 'Paracetamol or ibuprofen can help, if suitable for you. Always follow the leaflet dose and ask a pharmacist if unsure. Never place aspirin directly on the gum.',
                ua: 'Можуть допомогти парацетамол або ібупрофен, якщо вони вам підходять. Дотримуйтеся дозування з інструкції і за потреби порадьтеся з фармацевтом. Ніколи не кладіть аспірин на ясна.',
                ru: 'Могут помочь парацетамол или ибупрофен, если они вам подходят. Соблюдайте дозировку из инструкции и при необходимости посоветуйтесь с фармацевтом. Никогда не кладите аспирин на десну.',
                ro: 'Paracetamolul sau ibuprofenul pot ajuta, dacă vi se potrivesc. Respectați doza din prospect și întrebați farmacistul dacă nu sunteți sigur. Nu puneți niciodată aspirină direct pe gingie.',
              },
            },
            {
              title: { en: 'Rinse and clean gently', ua: 'Обережно прополощіть і почистьте', ru: 'Аккуратно прополощите и почистите', ro: 'Clătiți și curățați delicat' },
              text: {
                en: 'Rinse with warm salt water and floss gently around the tooth in case food is trapped.',
                ua: 'Прополощіть теплою солоною водою і обережно пройдіться ниткою біля зуба — можливо, там застрягла їжа.',
                ru: 'Прополощите тёплой солёной водой и аккуратно пройдитесь нитью возле зуба — возможно, там застряла еда.',
                ro: 'Clătiți cu apă caldă sărată și folosiți delicat ața dentară în jurul dintelui, în caz că s-au blocat resturi alimentare.',
              },
            },
            {
              title: { en: 'Use a cold compress for swelling', ua: 'Холодний компрес при набряку', ru: 'Холодный компресс при отёке', ro: 'Compresă rece pentru umflătură' },
              text: {
                en: 'Apply it to the outside of the cheek. Avoid heat, which can make an infection worse.',
                ua: 'Прикладайте ззовні до щоки. Уникайте тепла — воно може посилити інфекцію.',
                ru: 'Прикладывайте снаружи к щеке. Избегайте тепла — оно может усилить инфекцию.',
                ro: 'Aplicați-o pe exteriorul obrazului. Evitați căldura, care poate agrava o infecție.',
              },
            },
            {
              title: { en: 'Book a dental appointment', ua: 'Запишіться до стоматолога', ru: 'Запишитесь к стоматологу', ro: 'Faceți o programare la dentist' },
              text: {
                en: 'Painkillers only mask the symptoms. The cause needs to be diagnosed and treated.',
                ua: 'Знеболювальні лише маскують симптоми. Причину потрібно діагностувати і лікувати.',
                ru: 'Обезболивающие лишь маскируют симптомы. Причину нужно диагностировать и лечить.',
                ro: 'Analgezicele doar maschează simptomele. Cauza trebuie diagnosticată și tratată.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'Common causes and possible treatment',
        ua: 'Поширені причини і можливе лікування',
        ru: 'Частые причины и возможное лечение',
        ro: 'Cauze frecvente și tratament posibil',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              title: { en: 'Decay:', ua: 'Карієс:', ru: 'Кариес:', ro: 'Carie:' },
              text: {
                en: 'caught early, it is usually treated with a filling after a [dental examination](/prices#consultations).',
                ua: 'на ранній стадії зазвичай лікується пломбою після [стоматологічного огляду](/prices#consultations).',
                ru: 'на ранней стадии обычно лечится пломбой после [стоматологического осмотра](/prices#consultations).',
                ro: 'depistată devreme, se tratează de obicei cu o plombă după o [examinare stomatologică](/prices#consultations).',
              },
            },
            {
              title: { en: 'Inflamed or infected nerve:', ua: 'Запалений чи інфікований нерв:', ru: 'Воспалённый или инфицированный нерв:', ro: 'Nerv inflamat sau infectat:' },
              text: {
                en: 'often treated with [root canal treatment](/root-canal-london), which aims to save the tooth.',
                ua: 'часто лікується [лікуванням каналів](/root-canal-london), метою якого є зберегти зуб.',
                ru: 'часто лечится [лечением каналов](/root-canal-london), цель которого — сохранить зуб.',
                ro: 'se tratează adesea prin [tratament de canal](/root-canal-london), care urmărește salvarea dintelui.',
              },
            },
            {
              title: { en: 'Badly damaged tooth:', ua: 'Сильно зруйнований зуб:', ru: 'Сильно разрушенный зуб:', ro: 'Dinte grav afectat:' },
              text: {
                en: 'if it cannot be restored, [tooth extraction](/services/extraction) may be the safest option, followed by a discussion about replacing it.',
                ua: 'якщо його неможливо відновити, найбезпечнішим варіантом може бути [видалення зуба](/services/extraction) з подальшим обговоренням заміни.',
                ru: 'если его невозможно восстановить, самым безопасным вариантом может быть [удаление зуба](/services/extraction) с последующим обсуждением замены.',
                ro: 'dacă nu poate fi restaurat, [extracția dentară](/services/extraction) poate fi opțiunea cea mai sigură, urmată de discutarea înlocuirii.',
              },
            },
            {
              title: { en: 'Gum problems or wisdom teeth:', ua: 'Проблеми з яснами чи зуби мудрості:', ru: 'Проблемы с дёснами или зубы мудрости:', ro: 'Probleme gingivale sau măsele de minte:' },
              text: {
                en: 'cleaning, medication or further treatment may be advised depending on the findings.',
                ua: 'залежно від результатів огляду можуть порадити чистку, ліки чи подальше лікування.',
                ru: 'в зависимости от результатов осмотра могут посоветовать чистку, лекарства или дальнейшее лечение.',
                ro: 'în funcție de constatări, se pot recomanda igienizare, medicamente sau tratament suplimentar.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'Where to get help in London',
        ua: 'Куди звернутися в Лондоні',
        ru: 'Куда обратиться в Лондоне',
        ro: 'Unde găsiți ajutor în Londra',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'If you have an NHS dentist, contact them first. If you do not, or cannot get an appointment quickly, a private clinic can usually see you sooner. Dentico Clinic in Angel offers urgent appointments where available — [book online or contact us](/contact) by phone or WhatsApp. You can check consultation fees on our [prices page](/prices).',
            ua: 'Якщо у вас є стоматолог NHS, спершу зверніться до нього. Якщо ні або швидкого запису немає, приватна клініка зазвичай прийме раніше. Dentico Clinic в Angel пропонує термінові прийоми за наявності вільного часу — [запишіться онлайн або зв’яжіться з нами](/contact) телефоном чи у WhatsApp. Вартість консультації — на [сторінці цін](/prices).',
            ru: 'Если у вас есть стоматолог NHS, сначала обратитесь к нему. Если нет или быстрой записи нет, частная клиника обычно примет раньше. Dentico Clinic в Angel предлагает срочные приёмы при наличии свободного времени — [запишитесь онлайн или свяжитесь с нами](/contact) по телефону или в WhatsApp. Стоимость консультации — на [странице цен](/prices).',
            ro: 'Dacă aveți un dentist NHS, contactați-l mai întâi. Dacă nu aveți sau nu obțineți rapid o programare, o clinică privată vă poate vedea de obicei mai devreme. Dentico Clinic din Angel oferă programări urgente în funcție de disponibilitate — [programați-vă online sau contactați-ne](/contact) telefonic sau pe WhatsApp. Tariful consultației îl găsiți pe [pagina de prețuri](/prices).',
          },
        },
      ],
    },
  ],
  faqs: [
    {
      q: { en: 'Can toothache go away on its own?', ua: 'Чи може зубний біль минути сам?', ru: 'Может ли зубная боль пройти сама?', ro: 'Poate trece durerea de dinți de la sine?' },
      a: {
        en: 'Pain can ease temporarily, but the cause usually remains. If a nerve dies, pain may stop while infection continues, so it is best to be checked.',
        ua: 'Біль може тимчасово вщухнути, але причина зазвичай залишається. Якщо нерв відмирає, біль може зникнути, а інфекція — розвиватися, тому краще перевіритися.',
        ru: 'Боль может временно утихнуть, но причина обычно остаётся. Если нерв отмирает, боль может пройти, а инфекция — развиваться, поэтому лучше провериться.',
        ro: 'Durerea se poate atenua temporar, dar cauza rămâne de obicei. Dacă nervul moare, durerea poate înceta în timp ce infecția continuă, așa că e bine să vă verificați.',
      },
    },
    {
      q: { en: 'Should I take antibiotics for toothache?', ua: 'Чи потрібно пити антибіотики при зубному болю?', ru: 'Нужно ли пить антибиотики при зубной боли?', ro: 'Trebuie să iau antibiotice pentru durerea de dinți?' },
      a: {
        en: 'Only if prescribed. Antibiotics do not treat most toothache and are used only for certain spreading infections alongside dental treatment.',
        ua: 'Лише за призначенням. Антибіотики не лікують більшість випадків зубного болю і застосовуються лише при певних поширених інфекціях разом зі стоматологічним лікуванням.',
        ru: 'Только по назначению. Антибиотики не лечат большинство случаев зубной боли и применяются лишь при определённых распространяющихся инфекциях вместе со стоматологическим лечением.',
        ro: 'Doar la prescripție. Antibioticele nu tratează majoritatea durerilor de dinți și se folosesc doar pentru anumite infecții care se extind, împreună cu tratamentul dentar.',
      },
    },
    {
      q: { en: 'What should I do if a tooth is knocked out?', ua: 'Що робити, якщо зуб вибитий?', ru: 'Что делать, если зуб выбит?', ro: 'Ce fac dacă un dinte a căzut în urma unei lovituri?' },
      a: {
        en: 'Hold it by the crown, not the root, rinse briefly if dirty, and keep it in milk or saliva. See a dentist as soon as possible, ideally within an hour.',
        ua: 'Тримайте його за коронку, а не за корінь, коротко промийте, якщо брудний, і покладіть у молоко або слину. Якнайшвидше зверніться до стоматолога, бажано протягом години.',
        ru: 'Держите его за коронку, а не за корень, кратко промойте, если грязный, и положите в молоко или слюну. Как можно скорее обратитесь к стоматологу, желательно в течение часа.',
        ro: 'Țineți-l de coroană, nu de rădăcină, clătiți-l scurt dacă e murdar și păstrați-l în lapte sau salivă. Mergeți la dentist cât mai repede, ideal în decurs de o oră.',
      },
    },
    {
      q: { en: 'Can I be seen the same day in London?', ua: 'Чи можна потрапити на прийом у Лондоні того ж дня?', ru: 'Можно ли попасть на приём в Лондоне в тот же день?', ro: 'Pot fi consultat în aceeași zi în Londra?' },
      a: {
        en: 'Many private clinics, including Dentico Clinic, try to offer same-day urgent appointments when available. Contact the clinic as early as possible.',
        ua: 'Багато приватних клінік, зокрема Dentico Clinic, намагаються запропонувати терміновий прийом того ж дня за наявності часу. Зверніться до клініки якомога раніше.',
        ru: 'Многие частные клиники, включая Dentico Clinic, стараются предложить срочный приём в тот же день при наличии времени. Обратитесь в клинику как можно раньше.',
        ro: 'Multe clinici private, inclusiv Dentico Clinic, încearcă să ofere programări urgente în aceeași zi, în funcție de disponibilitate. Contactați clinica cât mai devreme.',
      },
    },
    {
      q: { en: 'When should I call NHS 111 or 999?', ua: 'Коли телефонувати NHS 111 або 999?', ru: 'Когда звонить NHS 111 или 999?', ro: 'Când sun la NHS 111 sau 999?' },
      a: {
        en: 'Call NHS 111 for urgent dental advice when you cannot reach a dentist. Call 999 for swelling affecting breathing, swallowing or vision, or uncontrolled bleeding.',
        ua: 'NHS 111 — для термінової консультації, коли не вдається зв’язатися зі стоматологом. 999 — при набряку, що заважає дихати, ковтати чи бачити, або при неконтрольованій кровотечі.',
        ru: 'NHS 111 — для срочной консультации, когда не удаётся связаться со стоматологом. 999 — при отёке, мешающем дышать, глотать или видеть, либо при неконтролируемом кровотечении.',
        ro: 'Sunați la NHS 111 pentru sfaturi urgente când nu puteți contacta un dentist. Sunați la 999 pentru umflături care afectează respirația, înghițirea sau vederea, ori sângerare necontrolată.',
      },
    },
  ],
  readAlso: [
    {
      to: '/emergency-dentist-london',
      label: { en: 'Emergency Dentist in London', ua: 'Невідкладний стоматолог у Лондоні', ru: 'Неотложный стоматолог в Лондоне', ro: 'Dentist de urgență în Londra' },
    },
    {
      to: '/root-canal-london',
      label: { en: 'Root Canal Treatment', ua: 'Лікування каналів', ru: 'Лечение каналов', ro: 'Tratament de canal' },
    },
  ],
}
